import * as moment from 'moment';
import { formatUtcTimestampLocal } from '@util/utc-timestamp';
import {Parser} from 'json2csv';
import {formatISO} from 'date-fns'
import {save} from '@util/save-as';
import {saveXLSX} from '@util/save-xlsx';
import { VwReservation } from '../reservation';
import { tr } from '@util/tr';
import { locale } from '@util/locale';
import { DWSFormOptions } from '../experience-dws';
import { ExperiencePriceLabel } from '@interfaces/experience-price-label';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ReservationAdditionalQuestionAnswer,
  ReservationAdditionalQuestionExportPayload,
} from '@services/dws/additional-questions.service';

export namespace ReservationExporting {

  export interface Data {
    id: string;
    wineryId: string;
    titleEn: string;
    titleIt: string;
    reservationDate: string;
    experienceDate: string;
    birthDate: string;
    duration: string;
    languageEn: string;
    languageIt: string;
    guestcount01: number;
    guestcount02: number;
    experiencePriceExtras: {quantity: number}[];
    experiencePriceLabels: {quantity: number}[];
    grossTotalCents: number;
    discountCode: string;
    discountCents: number;
    netTotalCents: number;
    giftCoupon: string;
    origin: string;
    paid: string;
    paymentMethodEn: string;
    paymentMethodIt: string;
    roomName: string;
    employeeName: string;
    state: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    message: string;
    notes: string;
    country: string;
    additionalQuestionAnswers?: Record<string, string>;
  }

  export type ExportRow = VwReservation & { additionalQuestionAnswers?: Record<string, string> };

  const normalizeExportId = (id: string): string => id.trim().toLowerCase();

  const pickQuestionTranslation = (texts: Record<string, string> | undefined, lang: string): string => {
    if (!texts) {
      return '';
    }
    const normalizedLang = (lang || 'it').toLowerCase();
    if (texts[normalizedLang]?.trim()) {
      return texts[normalizedLang].trim();
    }
    if (texts.en?.trim()) {
      return texts.en.trim();
    }
    if (texts.it?.trim()) {
      return texts.it.trim();
    }
    return Object.values(texts).find((value) => value?.trim())?.trim() || '';
  };

  const formatAnswerText = (answer: ReservationAdditionalQuestionAnswer, lang: string): string => {
    const snap = answer.answerSnapshot || {};
    const displayText = snap['displayText'];
    if (displayText != null && String(displayText).trim()) {
      return String(displayText).trim();
    }
    const text = snap['text'];
    if (text != null && String(text).trim()) {
      return String(text).trim();
    }
    const freeText = snap['freeText'];
    if (freeText != null && String(freeText).trim()) {
      return String(freeText).trim();
    }
    const labels = (snap['displayLabels'] ?? snap['labels']) as unknown;
    if (Array.isArray(labels)) {
      const parts = labels
        .filter((label) => label != null && String(label).trim())
        .map((label) => String(label).trim());
      if (parts.length) {
        return parts.join(', ');
      }
    }
    return '';
  };

  const toExportColumns = (
    answers: ReservationAdditionalQuestionAnswer[],
    headers: string[],
    lang: string
  ): Record<string, string> => {
    const columns: Record<string, string> = {};
    headers.forEach((header) => {
      columns[header] = '';
    });
    answers.forEach((answer) => {
      let header = pickQuestionTranslation(answer.questionTextSnapshot, lang);
      if (!header) {
        header = 'Question';
      }
      columns[header] = formatAnswerText(answer, lang);
    });
    return columns;
  };

  const collectAdditionalQuestionHeadersFromAnswers = (
    answers: ReservationAdditionalQuestionAnswer[],
    lang: string
  ): string[] => {
    const headers: string[] = [];
    const seen = new Set<string>();
    answers.forEach((answer) => {
      let header = pickQuestionTranslation(answer.questionTextSnapshot, lang);
      if (!header) {
        header = 'Question';
      }
      if (!seen.has(header)) {
        seen.add(header);
        headers.push(header);
      }
    });
    return headers.sort((a, b) => a.localeCompare(b));
  };

  export const collectAdditionalQuestionHeaders = (rows: ExportRow[]): string[] => {
    const headers: string[] = [];
    const seen = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row.additionalQuestionAnswers || {}).forEach((header) => {
        if (!seen.has(header)) {
          seen.add(header);
          headers.push(header);
        }
      });
    });
    return headers;
  };

  const reservationExperienceDate = (reservation: VwReservation): string | undefined => {
    if (reservation.startDateTime) {
      return moment(reservation.startDateTime).format('YYYY-MM-DD');
    }
    if (reservation.date) {
      return moment(reservation.date).format('YYYY-MM-DD');
    }
    return undefined;
  };

  export const resolveExportDateRange = (
    reservations: VwReservation[],
    filters: { startTime?: string | Date; endTime?: string | Date }
  ): { start: string; end: string } => {
    const start = filters.startTime ? moment(filters.startTime).format('YYYY-MM-DD') : undefined;
    const end = filters.endTime ? moment(filters.endTime).format('YYYY-MM-DD') : undefined;
    if (start && end) {
      return { start, end };
    }

    const dates = reservations
      .map(reservationExperienceDate)
      .filter((date): date is string => !!date)
      .sort();
    if (dates.length === 0) {
      const today = moment().format('YYYY-MM-DD');
      return { start: today, end: today };
    }
    return {
      start: start || dates[0],
      end: end || dates[dates.length - 1],
    };
  };

  export const mergeAdditionalQuestionExport = (
    reservations: VwReservation[],
    payload: ReservationAdditionalQuestionExportPayload
  ): { rows: ExportRow[]; aqHeaders: string[] } => {
    const byId: Record<string, Record<string, string>> = {};
    Object.entries(payload.answersByReservationId || {}).forEach(([id, columns]) => {
      byId[normalizeExportId(id)] = columns;
    });
    const rows: ExportRow[] = reservations.map((reservation) => {
      const normalizedId = reservation.id ? normalizeExportId(reservation.id) : '';
      return {
        ...reservation,
        additionalQuestionAnswers: (normalizedId && byId[normalizedId]) || {},
      };
    });
    const aqHeaders = (payload.headers?.length
      ? payload.headers
      : collectAdditionalQuestionHeaders(rows)
    ).slice().sort((a, b) => a.localeCompare(b));
    return { rows, aqHeaders };
  };

  export const exportReservationIds = (reservations: VwReservation[]): string[] =>
    reservations
      .filter((reservation) => reservation.id && reservation.type !== 'appointment')
      .map((reservation) => reservation.id!);

  export const normalizeAdditionalQuestionExportPayload = (
    payload: ReservationAdditionalQuestionExportPayload | Record<string, unknown> | null | undefined
  ): ReservationAdditionalQuestionExportPayload => {
    const raw = payload || {};
    const answersByReservationId =
      (raw as ReservationAdditionalQuestionExportPayload).answersByReservationId ||
      (raw as Record<string, Record<string, Record<string, string>>>).answers_by_reservation_id ||
      {};
    return {
      headers: (raw as ReservationAdditionalQuestionExportPayload).headers || [],
      answersByReservationId,
    };
  };

  export const loadAdditionalQuestionsForExport = (
    reservations: VwReservation[],
    exportBatch: (reservationIds: string[]) => Observable<ReservationAdditionalQuestionExportPayload>,
    fetchAnswers: (reservationId: string) => Observable<ReservationAdditionalQuestionAnswer[]>
  ): Observable<{ rows: ExportRow[]; aqHeaders: string[] }> => {
    const reservationIds = exportReservationIds(reservations);
    if (reservationIds.length === 0) {
      return of({ rows: reservations as ExportRow[], aqHeaders: [] });
    }

    return exportBatch(reservationIds).pipe(
      map(normalizeAdditionalQuestionExportPayload),
      catchError((error) => {
        console.error('Additional questions batch export failed', error);
        return of({ headers: [], answersByReservationId: {} });
      }),
      switchMap((payload) => {
        const merged = mergeAdditionalQuestionExport(reservations, payload);
        if (merged.aqHeaders.length > 0) {
          return of(merged);
        }
        return enrichWithAdditionalQuestions(reservations, fetchAnswers);
      })
    );
  };

  /** @deprecated Prefer mergeAdditionalQuestionExport with batch export API — N+1 per reservation. */
  export const enrichWithAdditionalQuestions = (
    reservations: VwReservation[],
    fetchAnswers: (reservationId: string) => Observable<ReservationAdditionalQuestionAnswer[]>
  ): Observable<{ rows: ExportRow[]; aqHeaders: string[] }> => {
    const exportable = reservations.filter((reservation) => reservation.id && reservation.type !== 'appointment');
    if (exportable.length === 0) {
      return of({ rows: reservations as ExportRow[], aqHeaders: [] });
    }

    return forkJoin(
      exportable.map((reservation) =>
        fetchAnswers(reservation.id!).pipe(
          catchError(() => of([] as ReservationAdditionalQuestionAnswer[])),
          map((answers) => ({ id: normalizeExportId(reservation.id!), answers }))
        )
      )
    ).pipe(
      map((results) => {
        const lang = (locale().locale || 'it').toLowerCase();
        const answersById = new Map(results.map((result) => [result.id, result.answers]));
        const aqHeaders = collectAdditionalQuestionHeadersFromAnswers(
          results.reduce(
            (all, result) => all.concat(result.answers),
            [] as ReservationAdditionalQuestionAnswer[]
          ),
          lang
        );
        const rows: ExportRow[] = reservations.map((reservation) => {
          const answers = reservation.id
            ? answersById.get(normalizeExportId(reservation.id)) || []
            : [];
          return {
            ...reservation,
            additionalQuestionAnswers: toExportColumns(answers, aqHeaders, lang),
          };
        });
        return { rows, aqHeaders };
      })
    );
  };

  /** @deprecated Prefer enrichWithAdditionalQuestions — reservation-export API only covers manual reservations. */
  export const mergeAdditionalQuestionAnswers = (
    reservations: VwReservation[],
    exportRows: Data[]
  ): ExportRow[] => {
    const byId = new Map(
      exportRows.map((row) => [normalizeExportId(String(row.id)), row.additionalQuestionAnswers || {}])
    );
    return reservations.map((reservation) => ({
      ...reservation,
      additionalQuestionAnswers: byId.get(normalizeExportId(String(reservation.id!))) || {},
    }));
  };

  export const csvFields = (
    nLabels: number,
    nExtras: number,
    nParticipants: number,
    manualOrigins: any,
    paymentMethods: {[key: string]: any},
    rooms: {[key: string]: any},
    employees: {[key: string]: any},
    aqHeaders: string[] = []
  ) => [
    {value: 'id', label: tr('Reservation id:')},
    {value: (r: any) => r.type === 'appointment' ? tr('Appointment') : tr('Reservation'), label: tr('Type')},
    {value: (r: any) => r.type === 'appointment' ? '' : r.experienceTranslations?.find((e: any) => e.translation_id == 'TITLE')?._translations[locale().locale], label: tr('Experience')},
    {value: (r: any) => r.createdAt ? formatUtcTimestampLocal(r.createdAt, 'dd/MM/yyyy HH:mm') : undefined, label: tr('Reservation date')},
    {value: (r: any) => r.startDateTime ? moment(r.startDateTime).format('DD/MM/YYYY HH:mm') : (r.date && r.time ? moment(r.date + 'T' + r.time).format('DD/MM/YYYY HH:mm') : undefined), label: tr('Experience date')},
    {value: (r: any) => r.type === 'appointment' ? '' : ((r.experience || [{}])[0].duration || ''), label: tr('Duration')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.languageIso ? tr('lang_' + r.languageIso) : ''), label: tr('Language')},
    {value: (r: any) => r.type === 'appointment' ? '' : r.guestCount01, label: tr('Label')+' 1'},
    ...Array.from(Array(nLabels - 1).keys()).map(i => ({value: (r: any) => r.type === 'appointment' ? '' : (r.reservationPriceLabels?.find((l: any) => l.position == (i+1))?.quantity || 0), label: `Label ${i + 2}`})),
    ...Array.from(Array(nExtras).keys()).map(i => ({value: (r: any) => r.type === 'appointment' ? '' : (r.reservationPriceExtras?.find((l: any) => l.position == i)?.quantity || 0), label: `Extra ${i + 1}`})),
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.paymentMethodId ? paymentMethods[r.otherData.paymentMethodId] || '' : paymentMethods[r.optionalData?.paymentMethodId] || ''), label: tr('Terms of payment')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.payment2MethodId ? paymentMethods[r.otherData.payment2MethodId] || '' : paymentMethods[r.optionalData?.payment2MethodId] || ''), label: tr('Method of payment')},
    {value: (r: any) => r.type === 'appointment' ? '' : centsInCurrency(computeSubtotal(r)), label: tr('Subtotal')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.discountTotalCents ? formatDiscountCode(r) : ''), label: tr('Discount code')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.giftId ? centsInCurrency(r.giftAmountCents) : ''), label: tr('Gift')},
    {value: (r: any) => r.type === 'appointment' ? '' : centsInCurrency(r.otherData?.manualDiscountTotalCents || r.optionalData?.manualDiscountTotalCents || r.manualDiscountTotalCents || 0), label: tr('Additional discount')},
    {value: (r: any) => r.type === 'appointment' ? '' : centsInCurrency(r.netTotalCents || 0), label: tr('Total')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.paid || (r.netTotalCents == 0 && !!r.otherData?.giftCents) ? tr('Yes') : tr('No')), label: tr('Paid')},
    {value: (r: any) => r.type === 'appointment' ? '' : centsInCurrency(r.otherData?.paidTotalCents || r.optionalData?.paidTotalCents || (r.paid ? r.netTotalCents : 0) || 0), label: tr('Amount paid')},
    {value: (r: any) => r.type === 'appointment' ? '' : centsInCurrency(r.dueTotalCents || 0), label: tr('Amount due')},
    {value: (r: any) => r.type === 'appointment' ? '' : formatPaymentDateForExport(r), label: tr('Payment date')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.origin || manualOrigins.find((o: any) => o.id == r.manualOriginId)?.nameIt || 'Prenotazione Manuale'), label: tr('Origin')},
    {value: (r: any) => r.roomId ? rooms[r.roomId] || '' : '', label: tr('Room')},
    {value: (r: any) => r.employeeId ? employees[r.employeeId] || '' : '', label: tr('Employee')},
    {value: 'message', label: tr('Message')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.state ? tr(r.state[0]?.toUpperCase() + r.state.substring(1)) : ''), label: tr('Status')},
    {value: (r: any) => r.type === 'appointment' ? '' : ((r.otherData && "invoiceData" in r.otherData && r.otherData.invoiceData && r.otherData.invoiceData !== "{}") ? tr('Yes') : tr('No')), label: tr('Invoice requested')},
    {value: (r: any) => r.type === 'appointment' ? '' : ((r.otherData?.isSales === "true" || r.optionalData?.isSales === "true") ? tr('Yes') : tr('No')), label: tr('isSales')},
    {value: (r: any) => r.type === 'appointment' ? '' : (( r.otherData?.checkedIn === "true" || r.optionalData?.checkedIn === "true") ? tr('Yes') : tr('No')), label: tr('Check in')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.hasPaymentLink ? tr('Yes') : tr('No')), label: tr('Payment link')},
    {value: 'notes', label: tr('Internal notes')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.first_name, label: tr('Customer first name')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.last_name, label: tr('Customer last name')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.email, label: tr('Customer email')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.phone_number, label: tr('Customer phone number')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.country_iso, label: tr('Customer country of residence')},
    {value: (r: any) => r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.other_data?.birthDate ? moment(r.reservationContacts?.find((c: any) => c.contact_type == 'MASTER')?.other_data?.birthDate, 'YYYY-MM-DD').utc(true).format('DD/MM/YYYY') : undefined, label: tr('Customer date of birth')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).companyName : ''), label: tr('Company name')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).vat : ''), label: tr('VAT number')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).sdi : ''), label: tr('Unique code (SDI)')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).fiscalCode : ''), label: tr('Fiscal Code')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).billingCountry?.name : ''), label: tr('Country')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).billingAddress : ''), label: tr('Address')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).billingCity : ''), label: tr('City')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).billingProvince : ''), label: tr('Province')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData).billingZipCode : ''), label: tr('ZIP code')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.isTourOperator ? tr('Yes') : tr('No')), label: tr('Tour Operator')},
    {value: (r: any) => r.type === 'appointment' ? '' : (r.reservationContacts?.filter((c: any) => c.contact_type != 'MASTER')?.length || 0), label: tr('Additional contacts')},
    ...Array.from(Array(nParticipants).keys()).map(i => ({value: (r: any) => r.reservationContacts?.filter((c: any) => c.contact_type != 'MASTER')[i]?.email || '', label: tr(`Participant ${i + 1}`)})),
    ...aqHeaders.map((header) => ({
      value: (r: ExportRow) => r.additionalQuestionAnswers?.[header] ?? '',
      label: header,
    })),
  ];

  export const saveAsXLSX = (r: ExportRow[], formOptions: DWSFormOptions, aqHeaders: string[] = []) => {
    let maxLabel = r.reduce((max, res) => Math.max(max, res.reservationPriceLabels?.reduce((max, p) => Math.max(max, p.position + 1), 0) || 0), 1);
    let maxExtra = r.reduce((max, res) => Math.max(max, res.reservationPriceExtras?.reduce((max, p) => Math.max(max, p.position + 1), 0) || 0), 0);
    let maxParticipants = r.reduce((max, res) => Math.max(max, res.reservationContacts?.length || 0), 0) - 1;
    const xlsxData = r.map(d => mapToExcelData(d, maxLabel, maxExtra, maxParticipants, formOptions, aqHeaders));
    saveXLSX(xlsxData, `res-${formatISO(new Date, {representation: 'date'})}.xlsx`, {
      monetaryColumnHeaders: monetaryExportColumnHeaders(),
    });
  }

  export const mapToExcelData = (r: ExportRow, labelCount: number, extraCount: number, participantsCount: number, formOptions: DWSFormOptions, aqHeaders: string[] = []) => {
    let labels: {[key: string]: any} = {[tr('Label') + " 1"]: r.type === 'appointment' ? '' : r.guestCount01};
    let extras: {[key: string]: any} = {};
    let participantsEmails: {[key: string]: any} = {};
    let invoiceData = r.otherData?.invoiceData ? JSON.parse(r.otherData.invoiceData) : {};
    const labelsWithout1 = r.reservationPriceLabels?.filter(l => !l.islabel1) || [];
    for (let i = 1; i < labelCount; i++) {
      let key = tr('Label') + " " + (i + 1);
      let value = r.type === 'appointment' ? '' : (labelsWithout1.find(l => l.position == i)?.quantity || 0);
      labels[key] = value;
    }

    for (let i = 0; i < extraCount; i++) {
      let key = tr('Extra') + " " + (i + 1);
      let value = r.type === 'appointment' ? '' : (r.reservationPriceExtras?.find(l => l.position == i)?.quantity || 0);
      extras[key] = value;
    }

    for (let i = 0; i < participantsCount; i++) {
      let key = tr(`Participant`) + " " + (i + 1);
      let contact = r.reservationContacts?.filter(c => c.contact_type != 'MASTER')[i] || {};
      participantsEmails[key] = contact.email || '';
    }

    let countries: {[key: string]: any} = {},
    rooms: {[key: string]: any} = {},
    employees: {[key: string]: any} = {},
    paymentMethods: {[key: string]: any} = {};

    formOptions.countries.forEach(c => countries[c.iso!] = c.nameIt);
    formOptions.rooms.forEach(r => rooms[r.id!] = r.name);
    formOptions.employees.forEach(e => employees[e.id!] = e.firstName + ' ' + e.lastName);
    formOptions.paymentMethods.forEach(p => {
      paymentMethods[p.id!] = p.nameTranslations[locale().locale] || p.nameTranslations['it'];
      p.children?.forEach((p2: any) => {
        paymentMethods[p2.id!] = p2.nameTranslations[locale().locale] || p2.nameTranslations['it'];
      });
    });

    let contact = r.reservationContacts?.find(c => c.contact_type == 'MASTER') || {};

    const additionalQuestions: {[key: string]: string} = {};
    for (const header of aqHeaders) {
      additionalQuestions[header] = r.additionalQuestionAnswers?.[header] ?? '';
    }

    return {
      [tr("Reservation id:")]: r.id,
      [tr("Type")]: r.type === 'appointment' ? tr('Appointment') : tr('Reservation'),
      [tr("Experience")]: r.type === 'appointment' ? '' : (r.experienceTranslations?.find(e => e.translation_id == 'TITLE')?._translations[locale().locale] || ''),
      [tr("Reservation date")]: r.createdAt ? formatUtcTimestampLocal(r.createdAt, 'dd/MM/yyyy HH:mm') : undefined,
      [tr("Experience date")]: r.startDateTime ? moment(r.startDateTime).format('DD/MM/YYYY HH:mm') : (r.date && r.time ? moment(r.date + 'T' + r.time).format('DD/MM/YYYY HH:mm') : undefined),
      [tr("Duration")]: r.type === 'appointment' ? '' : ((r.experience || [{}])[0].duration || ''),
      [tr("Language")]: r.type === 'appointment' ? '' : (r.languageIso ? tr('lang_' + r.languageIso) : ''),
      ...labels,
      ...extras,
      [tr("Terms of payment")]: r.type === 'appointment' ? '' : (r.optionalData?.paymentMethodId ? paymentMethods[r.optionalData.paymentMethodId] || '' : paymentMethods[r.otherData?.paymentMethodId] || ''),
      [tr("Method of payment")]: r.type === 'appointment' ? '' : (r.optionalData?.payment2MethodId ? paymentMethods[r.optionalData.payment2MethodId] || '' : paymentMethods[r.otherData?.payment2MethodId] || ''),
      [tr("Subtotal")]: r.type === 'appointment' ? '' : centsInCurrency(computeSubtotal(r)),
      [tr("Discount code")]: r.type === 'appointment' ? '' : (r.discountTotalCents ? formatDiscountCode(r) : ''),
      [tr("Gift")]: r.type === 'appointment' ? '' : (r.giftId ? centsInCurrency(r.giftAmountCents) : ''),
      [tr("Additional discount")]: r.type === 'appointment' ? '' : centsInCurrency(r.otherData?.manualDiscountTotalCents || r.optionalData?.manualDiscountTotalCents || r.manualDiscountTotalCents || 0),
      [tr("Total")]: r.type === 'appointment' ? '' : centsInCurrency(r.netTotalCents || 0),
      [tr("Paid")]: r.type === 'appointment' ? '' : (r.paid || (r.netTotalCents == 0 && !!r.otherData?.giftCents) ? tr('Yes') : tr('No')),
      [tr("Amount paid")]: r.type === 'appointment' ? '' : centsInCurrency(r.otherData?.paidTotalCents || r.optionalData?.paidTotalCents || (r.paid ? r.netTotalCents : 0) || 0),
      [tr("Amount due")]: r.type === 'appointment' ? '' : centsInCurrency(r.dueTotalCents || 0),
      [tr("Payment date")]: r.type === 'appointment' ? '' : formatPaymentDateForExport(r),
      [tr("Origin")]: r.type === 'appointment' ? '' : (r.origin || (formOptions.manualOrigins.find(o => o.id == r.manualOriginId) as any)?.nameIt || 'Prenotazione Manuale'),
      [tr("Room")]: r.roomId ? rooms[r.roomId] || '' : '',
      [tr("Employee")]: r.employeeId ? employees[r.employeeId] || '' : '',
      [tr("Message")]: r.message,
      [tr("Status")]: r.type === 'appointment' ? '' : (r.state ? tr(r.state[0]?.toUpperCase() + r.state.substring(1)) : ''),
      [tr("Invoice requested")]: r.type === 'appointment' ? '' : ((r.otherData && "invoiceData" in r.otherData && r.otherData.invoiceData && r.otherData.invoiceData !== "{}") ? tr('Yes') : tr('No')),
      [tr("isSales")]: r.type === 'appointment' ? '' : ((r.otherData?.isSales === "true" || r.optionalData?.isSales === "true") ? tr('Yes') : tr('No')),
      [tr("Checked in")]: r.type === 'appointment' ? '' : ((r.otherData?.checkedIn === "true" || r.optionalData?.checkedIn === "true") ? tr('Yes') : tr('No')),
      [tr("Payment Link")]: r.type === 'appointment' ? '' : (r.hasPaymentLink ? tr('Yes') : tr('No')),
      [tr("Internal Note")]: r.notes,
      [tr("Customer first name")]: contact.first_name,
      [tr("Customer last name")]:  contact.last_name,
      [tr("Customer email")]: contact.email,
      [tr("Customer phone number")]: contact.phone_number,
      [tr("Customer country of residence")]: contact.country_iso ? countries[contact.country_iso] || '' : '',
      [tr("Customer birth date")]: contact.other_data?.birthDate ? moment(contact.other_data?.birthDate, 'YYYY-MM-DD').utc(true).format('DD/MM/YYYY') : undefined,
      [tr("Company name")]: r.type === 'appointment' ? '' : (invoiceData.companyName || ''),
      [tr("VAT number")]: r.type === 'appointment' ? '' : (invoiceData.vat || ''),
      [tr("invoiceFields.sdi")]: r.type === 'appointment' ? '' : (invoiceData.sdi || ''),
      [tr("Fiscal Code")]: r.type === 'appointment' ? '' : (invoiceData.fiscalCode || ''),
      [tr("Country")]: r.type === 'appointment' ? '' : (invoiceData.billingCountry?.name || ''),
      [tr("Address")]: r.type === 'appointment' ? '' : (invoiceData.billingAddress || ''),
      [tr("City")]: r.type === 'appointment' ? '' : (invoiceData.billingCity || ''),
      [tr("Province")]: r.type === 'appointment' ? '' : (invoiceData.billingProvince || ''),
      [tr("Zipcode")]: r.type === 'appointment' ? '' : (invoiceData.billingZipCode || ''),
      [tr("Tour Operator")]: r.type === 'appointment' ? '' : (r.isTourOperator ? tr('Yes') : tr('No')),
      [tr("Additional Guests")]: r.type === 'appointment' ? '' : (r.reservationContacts?.filter(c => c.contact_type != 'MASTER')?.length || 0),
      ...participantsEmails,
      ...additionalQuestions,
    }
  }

  export const saveAsCSV = (data: ExportRow[], formOptions: DWSFormOptions, aqHeaders: string[] = []) => {
    let maxLabel = data.reduce((max, res) => Math.max(max, res.reservationPriceLabels?.reduce((max, p) => Math.max(max, p.position + 1), 0) || 0), 1);
    let maxExtra = data.reduce((max, res) => Math.max(max, res.reservationPriceExtras?.reduce((max, p) => Math.max(max, p.position + 1), 0) || 0), 0);
    let maxParticipants = data.reduce((max, res) => Math.max(max, res.reservationContacts?.length || 0), 0) - 1;

    let countries: {[key: string]: any} = {},
    rooms: {[key: string]: any} = {},
    employees: {[key: string]: any} = {},
    paymentMethods: {[key: string]: any} = {};

    formOptions.countries.forEach(c => countries[c.iso!] = c.nameIt);
    formOptions.rooms.forEach(r => rooms[r.id!] = r.name);
    formOptions.employees.forEach(e => employees[e.id!] = e.firstName + ' ' + e.lastName);
    formOptions.paymentMethods.forEach(p => {
      paymentMethods[p.id!] = p.nameTranslations[locale().locale] || p.nameTranslations['it'];
      p.children?.forEach((p2: any) => {
        paymentMethods[p2.id!] = p2.nameTranslations[locale().locale] || p2.nameTranslations['it'];
      });
    });

    const fields = ReservationExporting.csvFields(
      maxLabel,
      maxExtra,
      maxParticipants,
      formOptions.manualOrigins,
      paymentMethods,
      rooms,
      employees,
      aqHeaders
    );
    // Semicolon: European Excel expects list separator ; — avoids 102.25 being parsed as a date with comma decimal locale
    const parser = new Parser({ delimiter: ';', fields });
    const csvRows = parser.parse(data);
    save('\uFEFF' + csvRows).as(`res-${formatISO(new Date, {representation: 'date'})}.csv`, 'text/csv; charset=utf-8 ');
  }
}


/** Euro amount for export: 2 decimal places, stable for Excel (avoids float noise in SUM). */
const centsInCurrency = (cents: unknown = 0): number => {
  const c = Number(cents);
  const n = Number.isFinite(c) ? Math.round(c) : 0;
  return n / 100;
};

const formatDiscountCode = (r: any): string => {
  const code = r.otherData?.coupon || r.otherData?.couponCode || r.optionalData?.coupon || r.optionalData?.couponCode || '';
  const percent = r.otherData?.coupon_discount_percent ?? r.optionalData?.coupon_discount_percent;
  if (percent != null && percent !== '' && Number.isFinite(Number(percent))) return `${code} - ${percent}%`;
  return `${code} - ${centsInCurrency(r.discountTotalCents)}€`;
};

/** Column titles (current locale) for monetary cells — must match mapToExcelData / csvFields labels. */
const monetaryExportColumnHeaders = (): string[] => [
  tr('Subtotal'),
  tr('Gift'),
  tr('Additional discount'),
  tr('Total'),
  tr('Amount paid'),
  tr('Amount due'),
];

/** WS-2190: Format paidAt for export as DD/MM/YYYY HH:mm, empty if not paid. */
const formatPaymentDateForExport = (r: VwReservation): string | undefined => {
  const paidAt = r.paidAt || (r as any).otherData?.paidAt;
  if (!paidAt) return undefined;
  const formatted = formatUtcTimestampLocal(paidAt, 'dd/MM/yyyy HH:mm');
  return formatted || undefined;
};

const computeSubtotal = (r: VwReservation): number => {
  const raw =
    (r.reservationPriceLabels?.reduce((sum: number, l: ExperiencePriceLabel) =>
      sum + (l.quantity * (l.price_cents || r.experiencePrices?.find(p => p.position == l.position && p.price_type == 'LABEL')?.price_cents || 0))
    , 0) || 0
  ) + (
    r.reservationPriceExtras?.reduce((sum: number, e: any) =>
      sum + (e.quantity * (e.price_cents || r.experiencePrices?.find(p => p.position == e.position && p.price_type == 'EXTRA')?.price_cents || 0))
    , 0) || 0
  ) + ( r.reservationPriceLabels?.find(l => l.islabel1)
    ? 0
    : r.experiencePrices?.find(p => p.position == 0 && p.price_type == 'LABEL')?.price_cents || 0
  );
  return Math.round(raw);
};


export type ReservationExporting = ReservationExporting.Data;

